import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Stack } from 'react-bootstrap'
import type { Chat } from '@ztechapps/common.contract'
import { useMergeChat } from '@ztechapps/chat.common'
import { useToast } from '@ztechapps/common.hook'
import type { SelectOption } from '@ztechapps/webui.input'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { BusyButton } from '@ztechapps/webui.button'
import { Field, FormModal } from '@ztechapps/webui.form'
import { ConversationSelect } from '../ConversationSelect'
import { useConversationInboxContext } from './Context'

type MergeFormValues = { target?: SelectOption }

type MergeConversationProps = {
  /**
   * The currently open conversation. It becomes the merge SOURCE — it keeps its messages, the picked
   * target receives a note with its last message, and it is retired as a tombstone.
   */
  sourceChat?: Chat
}

const MergeTrigger = ({ onClick }: ModalTriggerProps) => (
  <BusyButton icon="code-merge" text="Merge into..." onClick={onClick} />
)

const validate = ({ target }: MergeFormValues) =>
  target?._id ? {} : { target: 'Pick the conversation to merge into.' }

export const MergeConversation = ({ sourceChat }: MergeConversationProps) => {
  const navigate = useNavigate()
  const { conversationItemRoute } = useConversationInboxContext()
  const { showSuccess } = useToast()
  const [merge] = useMergeChat()

  // A refused merge rejects, so Form toasts it once and keeps the modal open; a resolved
  // submit closes the modal through FormModal's afterSubmit.
  const onSubmit = async ({ target }: MergeFormValues) => {
    await merge({ sourceChatId: sourceChat?._id, targetChatId: target?._id })
    showSuccess({ message: 'Conversation merged.' })
    navigate(conversationItemRoute.altPath({ _id: target?.externalId ?? target?._id }))
  }

  return (
    <FormModal<MergeFormValues>
      trigger={MergeTrigger}
      title="Merge this conversation into..."
      saveText="Merge"
      initialValues={{}}
      validate={validate}
      onSubmit={onSubmit}
    >
      <Stack gap={2}>
        <Field
          name="target"
          label="Merge into"
          component={ConversationSelect}
          // Omits the source — same shape as libs/order/webui/src/order-inlineinput/RelatedOrderInlineInput.tsx:38.
          filter={{ _id: { $ne: sourceChat?._id } }}
          isClearable={false}
          onChange
        />
      </Stack>
    </FormModal>
  )
}
